/**
 * TaskTypeEditForm.tsx — the add/edit form for the Task Types admin dialog.
 *
 * Extracted from the retired TaskTypesPanelDialog (Run Mode Phase A); now
 * reused by the Operational Steps dialog (PR B). Fields:
 *
 *   Run Mode         — 'attended' (default) | 'continuous' (24/7). Phase A is
 *                      schema + admin only: the selector SAVES but the
 *                      scheduling engine ignores it until the Phase B engine
 *                      flip ships (an explicit note tells admins so).
 *   Attended Minutes — per-test PERSON minutes for continuous types (setup +
 *                      readings + teardown), 0..1440 or blank. Shown only when
 *                      Run Mode = continuous.
 *   Default Minutes  — TaskType.DefaultMinutes fallback machine time. Made
 *                      admin-writable with the Amendment 6 repository
 *                      allowlist fix (it was seed-script-only before).
 *
 * Colors: theme.schedule.* tokens — no hex literals.
 */

import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, Save } from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material';
import type { TaskType, Skill } from '../../../types/schedule';
import type {
  CreateTaskTypePayload,
  UpdateTaskTypePayload,
} from '../../../services/scheduleTaxonomy';
// Phase 4.4 (REQ-02): Category picklist mirrors the test catalog — single source.
import { SCHEDULE_CATEGORY_OPTIONS } from '../../../constants/scheduleEnums';
import { continuousSpanDays, type TaskRunMode } from '../../../utils/runMode';

const LAB_SECTIONS = ['Rock', 'Triaxial', 'DSS-DRS', 'Oedometer', 'Ring Shear', 'Soil', 'Other'];

// ── Form state ────────────────────────────────────────────────────────────────

export interface TaskTypeFormState {
  name: string;
  category: string;
  /** Phase 4.2: task type grouping subcategory (REQ-02). */
  subcategory: string;
  labSection: string;
  presetDurationDays: string;
  defaultMachineCount: string;
  sortOrder: string;
  /** Lab Taxonomy 2026 / D8: required skill ('' = no skill required). */
  skillKey: string;
  /** Run Mode Phase A: 'attended' | 'continuous'. */
  runMode: TaskRunMode;
  /** Run Mode Phase A: per-test person minutes, '' = null. */
  attendedMinutes: string;
  /** Amendment 6: TaskType.DefaultMinutes fallback machine time, '' = null. */
  defaultMinutes: string;
}

export const EMPTY_TASK_TYPE_FORM: TaskTypeFormState = {
  name: '',
  category: '',
  subcategory: '',
  labSection: '',
  presetDurationDays: '1',
  defaultMachineCount: '1',
  sortOrder: '0',
  skillKey: '',
  runMode: 'attended',
  attendedMinutes: '',
  defaultMinutes: '',
};

/** Build the form state from an existing TaskType row (edit mode). */
export function formFromTaskType(tt: TaskType): TaskTypeFormState {
  return {
    name: tt.Name,
    category: tt.Category ?? '',
    subcategory: tt.Subcategory ?? '',
    labSection: tt.LabSection ?? '',
    presetDurationDays: String(tt.PresetDurationDays ?? 1),
    defaultMachineCount: String(tt.DefaultMachineCount ?? 1),
    sortOrder: String(tt.SortOrder ?? 0),
    skillKey: tt.SkillKey ?? '',
    runMode: tt.RunMode ?? 'attended',
    attendedMinutes: tt.AttendedMinutes != null ? String(tt.AttendedMinutes) : '',
    defaultMinutes: tt.DefaultMinutes != null ? String(tt.DefaultMinutes) : '',
  };
}

/**
 * Client-side validation mirroring the backend's 400s.
 * Returns an error message, or null when the form is valid.
 */
export function validateTaskTypeForm(form: TaskTypeFormState): string | null {
  const name = form.name.trim();
  if (!name) return 'Name is required';
  if (name.length > 200) return 'Name must be 200 characters or fewer';
  const durationDays = parseInt(form.presetDurationDays, 10);
  if (!Number.isInteger(durationDays) || durationDays < 1) {
    return 'Preset Duration Days must be an integer >= 1';
  }
  if (form.attendedMinutes.trim() !== '') {
    const att = parseInt(form.attendedMinutes, 10);
    if (!Number.isInteger(att) || att < 0 || att > 1440 || String(att) !== form.attendedMinutes.trim()) {
      return 'Attended Minutes must be a whole number between 0 and 1440 (or blank)';
    }
  }
  if (form.defaultMinutes.trim() !== '') {
    const dm = parseInt(form.defaultMinutes, 10);
    if (!Number.isInteger(dm) || dm < 0 || String(dm) !== form.defaultMinutes.trim()) {
      return 'Default Minutes must be a whole number >= 0 (or blank)';
    }
  }
  return null;
}

/** Build the create/update payload from a validated form. */
export function payloadFromTaskTypeForm(
  form: TaskTypeFormState,
): CreateTaskTypePayload & UpdateTaskTypePayload {
  return {
    Name: form.name.trim(),
    Category: form.category.trim() || undefined,
    Subcategory: form.subcategory.trim() || null,
    LabSection: form.labSection || undefined,
    PresetDurationDays: parseInt(form.presetDurationDays, 10),
    DefaultMachineCount: parseInt(form.defaultMachineCount, 10) || 1,
    SortOrder: parseInt(form.sortOrder, 10) || 0,
    // Lab Taxonomy 2026 / D8: '' = no skill required → explicit null clears it.
    SkillKey: form.skillKey || null,
    // Run Mode Phase A — saved now, honored by the engine in Phase B.
    RunMode: form.runMode,
    AttendedMinutes: form.attendedMinutes.trim() !== '' ? parseInt(form.attendedMinutes, 10) : null,
    // Amendment 6: DefaultMinutes is now admin-writable ('' = null clears it).
    DefaultMinutes: form.defaultMinutes.trim() !== '' ? parseInt(form.defaultMinutes, 10) : null,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  mode: 'add' | string;
  form: TaskTypeFormState;
  setForm: React.Dispatch<React.SetStateAction<TaskTypeFormState>>;
  skills: Skill[];
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

export const TaskTypeEditForm: React.FC<Props> = ({
  mode,
  form,
  setForm,
  skills,
  saving,
  onSave,
  onCancel,
}) => {
  const theme = useTheme();
  const isContinuous = form.runMode === 'continuous';
  const defaultMinutesNum =
    form.defaultMinutes.trim() !== '' ? parseInt(form.defaultMinutes, 10) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        p: 2,
        bgcolor: 'action.hover',
        borderRadius: 1,
        m: 1,
      }}
    >
      <TextField
        size="small"
        label="Name *"
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        sx={{ minWidth: 240, flexGrow: 1 }}
        inputProps={{ maxLength: 200 }}
        autoFocus
      />
      {/* Phase 4.4 (REQ-02): Category Select draws from the same
          SCHEDULE_CATEGORY_OPTIONS constant the test catalog uses.
          Only the category list is shared; task-type Name stays independent. */}
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Category</InputLabel>
        <Select
          label="Category"
          value={form.category}
          onChange={(e: SelectChangeEvent) =>
            setForm((f) => ({ ...f, category: e.target.value }))
          }
        >
          <MenuItem value="">— None —</MenuItem>
          {SCHEDULE_CATEGORY_OPTIONS.map((cat) => (
            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        label="Subcategory"
        value={form.subcategory}
        onChange={(e) => setForm((f) => ({ ...f, subcategory: e.target.value }))}
        sx={{ minWidth: 140 }}
        inputProps={{ maxLength: 100 }}
      />
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Lab Section</InputLabel>
        <Select
          label="Lab Section"
          value={form.labSection}
          onChange={(e: SelectChangeEvent) =>
            setForm((f) => ({ ...f, labSection: e.target.value }))
          }
        >
          <MenuItem value="">— None —</MenuItem>
          {LAB_SECTIONS.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        label="Preset Days *"
        type="number"
        value={form.presetDurationDays}
        onChange={(e) => setForm((f) => ({ ...f, presetDurationDays: e.target.value }))}
        sx={{ width: 105 }}
        inputProps={{ min: 1 }}
      />
      <TextField
        size="small"
        label="Machine Qty"
        type="number"
        value={form.defaultMachineCount}
        onChange={(e) => setForm((f) => ({ ...f, defaultMachineCount: e.target.value }))}
        sx={{ width: 110 }}
        inputProps={{ min: 1 }}
      />
      <TextField
        size="small"
        label="Sort Order"
        type="number"
        value={form.sortOrder}
        onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
        sx={{ width: 100 }}
        inputProps={{ min: 0 }}
      />
      {/* Amendment 6: fallback machine time — used when no capability row has
          a per-machine rate. Now saves properly (repository allowlist fix). */}
      <TextField
        size="small"
        label="Default Minutes"
        value={form.defaultMinutes}
        onChange={(e) => {
          const v = e.target.value;
          if (v !== '' && !/^\d+$/.test(v)) return;
          setForm((f) => ({ ...f, defaultMinutes: v }));
        }}
        sx={{ width: 130 }}
        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
        placeholder="e.g. 6300"
        helperText="Fallback machine time"
      />
      {/* Run Mode Phase A: the admin knob. Saves now; the engine honors it
          only when the Phase B scheduling flip ships. */}
      <FormControl size="small" sx={{ minWidth: 190 }}>
        <InputLabel>Run Mode</InputLabel>
        <Select
          label="Run Mode"
          value={form.runMode}
          onChange={(e: SelectChangeEvent) =>
            setForm((f) => ({ ...f, runMode: e.target.value as TaskRunMode }))
          }
        >
          <MenuItem value="attended">Attended (default)</MenuItem>
          <MenuItem value="continuous">Continuous (24/7)</MenuItem>
        </Select>
      </FormControl>
      {isContinuous && (
        <TextField
          size="small"
          label="Attended Minutes"
          value={form.attendedMinutes}
          onChange={(e) => {
            const v = e.target.value;
            if (v !== '' && !/^\d+$/.test(v)) return;
            setForm((f) => ({ ...f, attendedMinutes: v }));
          }}
          sx={{ width: 150 }}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4 }}
          placeholder="e.g. 120"
          helperText="Person minutes per test (reporting only — staffing books every working day of the run)"
        />
      )}
      {/* Lab Taxonomy 2026 / D8: the admin remap surface — which skill this test
          requires. "None" = no skill required (mentor gate passes for everyone). */}
      <FormControl size="small" sx={{ minWidth: 260 }}>
        <InputLabel>Required skill</InputLabel>
        <Select
          label="Required skill"
          value={form.skillKey}
          onChange={(e: SelectChangeEvent) =>
            setForm((f) => ({ ...f, skillKey: e.target.value }))
          }
        >
          <MenuItem value="">— None (no skill required) —</MenuItem>
          {skills.map((s) => (
            <MenuItem key={s.SkillKey} value={s.SkillKey}>
              {s.Name}
              {s.Category ? ` (${s.Category})` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {isContinuous && (
        <Typography
          variant="caption"
          sx={{ width: '100%', color: theme.schedule.maintenance, fontWeight: 600 }}
        >
          Continuous: runs 24/7 once started — machine time counts calendar days
          {defaultMinutesNum != null && defaultMinutesNum > 0
            ? ` (~${continuousSpanDays(defaultMinutesNum)} calendar days at ${defaultMinutesNum} min)`
            : ''}
          . New plans schedule this way immediately; existing plans keep their current
          spans until you run Sync now or Replan on the source (never silently).
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <IconButton
          size="small"
          color="primary"
          onClick={onSave}
          disabled={saving || !form.name.trim()}
          title={mode === 'add' ? 'Create task type' : 'Save changes'}
        >
          <Save fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onCancel} title="Cancel">
          <Close fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
